import type { GetEntityDataV3Query } from '@revelio/data-access';

import type { TreeData } from '../../../../filters/contexts/FilterProvider';
import type { TreeSelection } from '../../../../filters/types/filters';

/**
 * Finds a company node in tree data by numeric ID.
 * Searches recursively through the tree structure.
 */
const findCompanyNodeByNumericId = ({
  nodes,
  targetNumericId,
  expectedDepth = 2,
}: {
  nodes: TreeData[];
  targetNumericId: number;
  expectedDepth?: number;
}): TreeData | null => {
  for (const node of nodes) {
    if (!node) continue;

    // Extract numeric ID from node ID (format: rics_k10_rics_k400_companyId or similar)
    const nodeNumericId = node.id.split('_').pop();
    const numericId = Number(nodeNumericId);

    // Check if this node matches the target ID and is at the expected depth (company level)
    if (
      // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
      !isNaN(numericId) &&
      numericId === targetNumericId &&
      node.id.split('_').length - 1 === expectedDepth
    ) {
      return node;
    }

    // Recursively search children
    if (node.children) {
      const found = findCompanyNodeByNumericId({
        nodes: node.children,
        targetNumericId,
        expectedDepth,
      });
      if (found) return found;
    }
  }

  return null;
};

/**
 * Transforms competitor data from GraphQL response to TreeSelection format.
 * Attempts to find nested tree IDs by looking up companies in the tree data.
 * If a company is not found in the tree, creates a simplified ID format.
 *
 * @param competitors - Array of competitors from the GraphQL response
 * @param companyTreeData - Tree data for the COMPANY filter (used to look up nested IDs)
 * @returns Array of TreeSelection objects
 */
export const transformCompetitorsToSelections = ({
  competitors,
  companyTreeData,
}: {
  competitors: NonNullable<
    NonNullable<GetEntityDataV3Query['entityV3']>['competitors']
  >;
  companyTreeData: TreeData[] | null;
}): TreeSelection[] => {
  if (!competitors || competitors.length === 0) {
    return [];
  }

  return competitors
    .map((competitor): TreeSelection | null => {
      if (!competitor?.id) return null;

      const competitorId = competitor.id;
      const competitorName = competitor.longName || competitor.shortName || '';

      // Try to find the company in the tree data to get the nested ID
      let nestedId: string | null = null;

      if (companyTreeData) {
        const foundNode = findCompanyNodeByNumericId({
          nodes: companyTreeData,
          targetNumericId: competitorId,
          expectedDepth: 2,
        });

        if (foundNode) {
          nestedId = foundNode.id;
        }
      }

      // If not found in tree, create a simplified ID format
      // This is a fallback - ideally companies should be in the tree data
      const id = nestedId || `0_0_${competitorId}`;

      return {
        id,
        name: competitorName,
        levelId: 'company' as const,
        isCurrent: true,
      };
    })
    .filter((selection): selection is TreeSelection => selection !== null);
};
